import {useState, useRef} from 'react';

import {Skeleton, TablePagination, Tooltip} from '@mui/material';
import {DoneAll, Close, Remove} from '@mui/icons-material';

import {theme} from '../../types/theme/Theme';
import {contestsPaginationStyle} from '../contests/contests-table/contest-table-styles';
import {GetGroupGeneralLeaderboardResponse} from '../../api/responses/GroupsResponseTypes';

type GeneralLeaderboardTableProps = {
  data: GetGroupGeneralLeaderboardResponse;
  isLoading: boolean;
  totalEntries: number;
  page: number;
  rowsPerPage: number;
  onPageNoChange: (value: number) => void;
  onRowsPerPageChange: (value: number) => void;
};

function GeneralLeaderboardTable({data, isLoading, ...pagination}: GeneralLeaderboardTableProps) {
  const [hasShadow, setHasShadow] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    if (containerRef.current) {
      setHasShadow(containerRef.current.scrollLeft > 0);
    }
  };

  return (
    <div className="flex flex-col items-end justify-center w-full ">
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="bg-background-light allowScrollbar overflow-x-scroll rounded-xl w-full pb-3 pr-2 shadow-md "
      >
        <table className="min-w-full table-auto">
          <thead className="h-20">
            <tr className="border-b border-gray-800">
              <th className="sticky left-0 bg-background-light min-w-[84px] text-center font-semibold p-2">Rank</th>
              <th className="sticky left-[84px] min-w-[284px] max-w-[384px] bg-background-light text-left font-semibold p-2">
                User
              </th>
              {Object.entries(data.contestsMetadata)
                .sort(([idA], [idB]) => Number(idA) - Number(idB)) // Sorting by contestId
                .map(([id, metadata]) => (
                  <th key={id} className="text-center p-2 whitespace-nowrap w-[96px] min-w-[96px] max-w-[96px]">
                    <Tooltip title={metadata.name} placement="top" arrow>
                      <span>{metadata.name.length > 7 ? `${metadata.name.slice(0, 6)}...` : metadata.name}</span>
                    </Tooltip>
                  </th>
                ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-700">
            {!isLoading &&
              data.rankings.map((entry, i) => {
                const baseBg = i % 2 === 0 ? 'bg-[#282c50]' : 'bg-[#2d3a59]';
                return (
                  <tr
                    key={i + pagination.page * pagination.rowsPerPage}
                    className={`group ${baseBg} hover:bg-[#3b4868] transition-colors duration-300`}
                  >
                    <td
                      className={`sticky left-0 p-2 text-center ${baseBg} group-hover:bg-[#3b4868] transition-colors duration-300`}
                    >
                      {entry.rank}
                    </td>
                    <td
                      className={`sticky left-[84px] min-w-[284px] max-w-[384px] whitespace-nowrap p-2 ${baseBg} group-hover:bg-[#3b4868] transition-all duration-300 ${
                        hasShadow ? ' shadow-[6px_0_5px_-2px_rgba(0,0,0,0.6)]' : ''
                      }`}
                    >
                      {entry.displayName.length > 28 ? (
                        <>
                          <Tooltip title={entry.displayName} placement="top" arrow>
                            <span>{`${entry.displayName.slice(0, 27)}...`}</span>
                          </Tooltip>
                        </>
                      ) : (
                        entry.displayName
                      )}
                    </td>
                    {entry.contestEntries
                      .sort((a, b) => a.contestId - b.contestId)
                      .map((userContestEntry) => (
                        <td
                          key={`${entry.displayName}-${userContestEntry.contestId}`}
                          className="p-[6px] select-none transition-all"
                        >
                          {userContestEntry.score == data.contestsMetadata[userContestEntry.contestId].maxScore ? (
                            <Tooltip title="Solved" placement="top" arrow>
                              <div className="flex flex-col items-center justify-between gap-1 w-full h-full bg-green-600 px-1 py-2 bg-opacity-60 hover:bg-opacity-90 rounded-sm">
                                <div className="text-white font-semibold text-sm mt-1">{userContestEntry.score}</div>
                                <span className="text-white">
                                  <DoneAll sx={{fontSize: '14px'}} />
                                </span>
                              </div>
                            </Tooltip>
                          ) : userContestEntry.score > 0 &&
                            userContestEntry.score < data.contestsMetadata[userContestEntry.contestId].maxScore ? (
                            <Tooltip title="Partially solved" placement="top" arrow>
                              <div className="flex flex-col items-center justify-between gap-1 w-full h-full bg-yellow-400 px-1 py-2 bg-opacity-70 hover:bg-opacity-90 rounded-sm">
                                <div className="text-black font-semibold text-sm mt-1">{userContestEntry.score}</div>
                                <span className="text-black">
                                  <Remove sx={{fontSize: '14px'}} />
                                </span>
                              </div>
                            </Tooltip>
                          ) : (
                            <Tooltip title="No solutions" placement="top" arrow>
                              <div className="flex flex-col items-center justify-between gap-1 w-full h-full bg-red-600 text-white px-1 py-2 bg-opacity-60 hover:bg-opacity-90 rounded-sm">
                                <span className="text-white font-semibold text-sm mt-1">{userContestEntry.score}</span>
                                <span className="text-white">
                                  <Close sx={{fontSize: '14px'}} />
                                </span>
                              </div>
                            </Tooltip>
                          )}
                        </td>
                      ))}
                  </tr>
                );
              })}

            {isLoading &&
              Array.from({
                length:
                  pagination.page * pagination.rowsPerPage < pagination.totalEntries
                    ? Math.min(
                        pagination.totalEntries - pagination.page * pagination.rowsPerPage,
                        pagination.rowsPerPage
                      )
                    : 0,
              }).map((_, index) => (
                <tr key={index} className={`${index % 2 === 0 ? 'bg-[#282c50]' : 'bg-[#2d3a59]'}`}>
                  <td
                    className={`sticky left-0 p-2 ${
                      index % 2 === 0 ? 'bg-[#282c50]' : 'bg-[#2d3a59]'
                    } z-10 items-center`}
                    align="center"
                  >
                    <Skeleton animation="pulse" variant="text" width={48} />
                  </td>
                  <td className={`sticky left-[84px] ${index % 2 === 0 ? 'bg-[#282c50]' : 'bg-[#2d3a59]'} z-10 `}>
                    <Skeleton animation="pulse" variant="text" width={250} />
                  </td>
                  {Object.keys(data.contestsMetadata).map((contestId) => (
                    <td key={contestId} className="p-[6px] py-2">
                      <Skeleton animation="pulse" variant="rectangular" width={84} height={64} />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>

        {data && data.rankings.length === 0 && !isLoading && (
          <div className="p-5 text-center text-xl">No leaderboard entries found based on the selected criteria.</div>
        )}
      </div>

      <TablePagination
        sx={contestsPaginationStyle(theme)}
        rowsPerPageOptions={[7, 10, 15]}
        component="div"
        count={pagination.totalEntries}
        rowsPerPage={pagination.rowsPerPage}
        page={pagination.page}
        onPageChange={(event, page) => pagination.onPageNoChange(page)}
        onRowsPerPageChange={(event) => pagination.onRowsPerPageChange(parseInt(event.target.value, 10))}
      />
    </div>
  );
}

export default GeneralLeaderboardTable;
